// src/screens/AllPlays.tsx
import { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";
import { useNavigate } from "react-router-dom";
import Select, { MultiValue, SingleValue } from "react-select";
import { PlusCircle, Globe, Target, Users, Book } from "lucide-react";
import { useTheme } from "../context/ThemeProvider";
import i18n from "../i18n";
import { LogoLoader } from "../components/LogoLoader";

type Tag = { id: string; name: string };
type Play = {
  id: string;
  user_id: string;
  name: string;
  data: any;
  created_at: string;
  tag_ids?: string[];
  team_id?: string | null;
};
type Team = { id: string; team_name: string };
type Option = { value: string; label: string };

/* ===== i18n mini-hook ===== */
function useI18nSafe() {
  const [lng, setLng] = useState((i18n.resolvedLanguage || i18n.language || "fr").toLowerCase());
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key));
  return { t, lng };
}

export const AllPlays = () => {
  const { t, lng } = useI18nSafe();

  const [plays, setPlays] = useState<Play[]>([]);
  const [allPlays, setAllPlays] = useState<Play[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamById, setTeamById] = useState<Record<string, Team>>({});

  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTeamFilter, setSelectedTeamFilter] = useState<string>(""); // ""=Tous, "none"=Aucune
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }

      // Tags
      const { data: tagsData } = await supabase.from("play_tags").select("*");
      setTags(tagsData || []);

      // Équipes visibles via RLS (owner + membre)
      const { data: teamsData, error: teamErr } = await supabase
        .from("teams")
        .select("id, team_name")
        .order("team_name", { ascending: true });
      if (teamErr) {
        console.warn("[AllPlays] teams fetch error:", teamErr);
      }
      const visibleTeams = teamsData || [];
      setTeams(visibleTeams);
      setTeamById(Object.fromEntries(visibleTeams.map((t) => [t.id, t])));

      // Plays d'équipe (où je suis autorisé via RLS) + mes plays perso
      const teamIds = visibleTeams.map(t => t.id);

      // 1) plays des équipes visibles
      let teamPlays: Play[] = [];
      if (teamIds.length > 0) {
        const { data: tp, error: e1 } = await supabase
          .from("plays")
          .select("*")
          .in("team_id", teamIds)
          .order("created_at", { ascending: false });
        if (e1) console.warn("[AllPlays] team plays fetch error:", e1);
        teamPlays = (tp || []) as Play[];
      }

      // 2) mes plays perso (et mes plays en général)
      const { data: mine, error: e2 } = await supabase
        .from("plays")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      if (e2) console.warn("[AllPlays] my plays fetch error:", e2);

      // Fusion + dédoublonnage
      const merged = [...(mine || []), ...teamPlays];
      const uniqMap = new Map<string, Play>();
      for (const p of merged) {
        // normaliser tag_ids si string dans la DB
        let tagIds: any = (p as any).tag_ids;
        if (typeof tagIds === "string") {
          try { tagIds = JSON.parse(tagIds); } catch { tagIds = []; }
        }
        const norm: Play = { ...p, tag_ids: Array.isArray(tagIds) ? tagIds : [] };
        uniqMap.set(p.id, norm);
      }
      const uniqueSorted = Array.from(uniqMap.values()).sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      setAllPlays(uniqueSorted);
      setPlays(uniqueSorted);
      setLoading(false);
    };

    fetchData();
  }, []);

  useEffect(() => {
    const filtered = allPlays.filter((play) => {
      const matchName = (play.name || "").toLowerCase().includes(searchTerm.toLowerCase());
      const playTags = play.tag_ids || [];
      const matchTags =
        selectedTags.length === 0 || selectedTags.every((tagId) => playTags.includes(tagId));

      let matchTeam = true;
      if (selectedTeamFilter === "none") {
        matchTeam = !play.team_id;
      } else if (selectedTeamFilter && selectedTeamFilter !== "none") {
        matchTeam = play.team_id === selectedTeamFilter;
      }
      return matchName && matchTags && matchTeam;
    });
    setPlays(filtered);
  }, [searchTerm, selectedTags, selectedTeamFilter, allPlays]);

  const handleView = (playId: string) => navigate(`/play/${playId}`);
  const resetFilters = () => {
    setSearchTerm("");
    setSelectedTags([]);
    setSelectedTeamFilter("");
  };

  const isDark = resolvedTheme === "dark";
  const selectStyles = useMemo(
    () => ({
      control: (base: any, state: any) => ({
        ...base,
        backgroundColor: isDark ? "#111827" : "#ffffff",
        borderColor: isDark ? (state.isFocused ? "#4b5563" : "#374151") : (state.isFocused ? "#9ca3af" : "#d1d5db"),
        boxShadow: "none",
        ":hover": { borderColor: isDark ? "#4b5563" : "#9ca3af" },
        color: isDark ? "#e5e7eb" : "#111827",
      }),
      menu: (base: any) => ({ ...base, backgroundColor: isDark ? "#111827" : "#ffffff", color: isDark ? "#e5e7eb" : "#111827", zIndex: 30 }),
      option: (base: any, state: any) => ({
        ...base,
        backgroundColor: state.isFocused ? (isDark ? "#1f2937" : "#f3f4f6") : "transparent",
        color: isDark ? "#e5e7eb" : "#111827",
        ":active": { backgroundColor: isDark ? "#374151" : "#e5e7eb" },
      }),
      multiValue: (base: any) => ({ ...base, backgroundColor: isDark ? "#1f2937" : "#dbeafe" }),
      multiValueLabel: (base: any) => ({ ...base, color: isDark ? "#93c5fd" : "#1e3a8a" }),
      multiValueRemove: (base: any) => ({ ...base, ":hover": { backgroundColor: isDark ? "#374151" : "#bfdbfe", color: isDark ? "#e5e7eb" : "#1e3a8a" } }),
      placeholder: (base: any) => ({ ...base, color: isDark ? "#9ca3af" : "#6b7280" }),
      input: (base: any) => ({ ...base, color: isDark ? "#e5e7eb" : "#111827" }),
      singleValue: (base: any) => ({ ...base, color: isDark ? "#e5e7eb" : "#111827" }),
    }),
    [isDark]
  );

  const teamFilterOptions: Option[] = useMemo(() => {
    return [
      { value: "", label: t("allplays:filters.all_teams") },
      { value: "none", label: t("allplays:filters.no_team") },
      ...teams.map((t) => ({ value: t.id, label: t.team_name })),
    ];
  }, [teams, t]);

  const selectedTeamOption: Option | null = useMemo(() => {
    return teamFilterOptions.find((o) => o.value === selectedTeamFilter) || teamFilterOptions[0];
  }, [teamFilterOptions, selectedTeamFilter]);

  // Format date selon langue active
  const formatDateTime = (iso: string) => {
    const d = new Date(iso);
    const locale = lng === "fr" ? "fr-FR" : "en-US";
    return d.toLocaleString(locale, {
      year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  };

  // 🔥 Loader SwishData pendant les fetchs
  if (loading) {
    return <LogoLoader />;
  }

  return (
    <div className="min-h-screen w-full bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
      {/* ===== HEADER (sticky) ===== */}
      <div className="sticky top-0 z-40 bg-gray-50/95 dark:bg-gray-950/85 backdrop-blur border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-6xl mx-auto px-4 py-3 pt-[calc(env(safe-area-inset-top,0px)+0.75rem)] flex items-center justify-between gap-3">
          <h1 className="text-xl sm:text-2xl font-bold text-orange-600 dark:text-orange-400">
            {t("allplays:title")}
          </h1>

          {/* Actions (desktop) */}
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => navigate("/playcreator")}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-xl
                       focus:outline-none focus:ring-2 focus:ring-blue-400/60"
            >
              <PlusCircle className="h-5 w-5" />
              {t("allplays:actions.create")}
            </button>

            <button
              onClick={() => navigate("/complays")}
              className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-xl
                       focus:outline-none focus:ring-2 focus:ring-green-400/60"
            >
              <Globe className="h-5 w-5" />
              {t("allplays:actions.community")}
            </button>

            <button
              onClick={() => navigate("/playbooks")}
              className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-xl
                       focus:outline-none focus:ring-2 focus:ring-purple-400/60"
            >
              <Book className="h-5 w-5" />
              {t("allplays:actions.playbooks")}
            </button>
          </div>
        </div>
      </div>

      {/* ===== CONTENT ===== */}
      <div className="max-w-6xl mx-auto px-4 py-4">
        {/* Actions (mobile) */}
        <div className="md:hidden grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <button
            onClick={() => navigate("/playcreator")}
            className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-xl
                     focus:outline-none focus:ring-2 focus:ring-blue-400/60"
          >
            <PlusCircle className="h-5 w-5" />
            {t("allplays:actions.create")}
          </button>

          <button
            onClick={() => navigate("/complays")}
            className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-xl
                     focus:outline-none focus:ring-2 focus:ring-green-400/60"
          >
            <Globe className="h-5 w-5" />
            {t("allplays:actions.community")}
          </button>

          <button
            onClick={() => navigate("/playbooks")}
            className="flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-xl
                     focus:outline-none focus:ring-2 focus:ring-purple-400/60"
          >
            <Book className="h-5 w-5" />
            {t("allplays:actions.playbooks")}
          </button>
        </div>

        {/* Filtres (card) */}
        <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 mb-4 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Recherche */}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-800 dark:text-gray-200">
                {t("allplays:filters.search_label") ?? t("allplays:filters.search_placeholder")}
              </label>
              <input
                type="text"
                placeholder={t("allplays:filters.search_placeholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border px-3 py-2 rounded-xl bg-white border-gray-300 text-gray-900 placeholder-gray-400
                         focus:outline-none focus:ring-2 focus:ring-orange-400/60
                         dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
              />
            </div>

            {/* Reset */}
            <div className="flex md:items-end">
              <button
                onClick={resetFilters}
                className="w-full px-4 py-2 rounded-xl bg-gray-200 hover:bg-gray-300 text-gray-800
                         dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-100
                         focus:outline-none focus:ring-2 focus:ring-gray-400/40"
              >
                {t("allplays:filters.reset")}
              </button>
            </div>

            {/* Tags */}
            <div>
              <label className="block mb-2 font-medium flex items-center gap-2">
                <Target className="w-4 h-4" />
                {t("allplays:filters.tags_label")}
              </label>
              <Select<Option, true>
                isMulti
                options={tags.map((tag) => ({ value: tag.id, label: tag.name }))}
                value={tags
                  .filter((tag) => selectedTags.includes(tag.id))
                  .map((tag) => ({ value: tag.id, label: tag.name }))}
                onChange={(selected: MultiValue<Option>) =>
                  setSelectedTags(selected.map((o) => o.value))
                }
                styles={selectStyles}
                placeholder={t("allplays:filters.tags_placeholder")}
                classNamePrefix="react-select"
              />
            </div>

            {/* Team */}
            <div>
              <label className="block mb-2 font-medium flex items-center gap-2">
                <Users className="w-4 h-4" />
                {t("allplays:filters.team_label")}
              </label>
              <Select<Option, false>
                isMulti={false}
                options={teamFilterOptions}
                value={selectedTeamOption}
                onChange={(opt: SingleValue<Option>) =>
                  setSelectedTeamFilter(opt?.value ?? "")
                }
                styles={selectStyles}
                classNamePrefix="react-select"
              />
            </div>
          </div>
        </div>

        {/* Résultats */}
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {t("allplays:results.count", { count: plays.length }) ?? `${plays.length} plays`}
          </div>
        </div>

        {plays.length === 0 ? (
          <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 text-center shadow-sm">
            <p className="text-gray-600 dark:text-gray-400">{t("allplays:empty")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {plays.map((play) => {
              const team = play.team_id ? teamById[play.team_id] : null;

              return (
                <div
                  key={play.id}
                  className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-4
                           hover:shadow transition cursor-pointer"
                  onClick={() => handleView(play.id)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleView(play.id); }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h2 className="text-lg font-semibold truncate">{play.name}</h2>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        {t("allplays:card.created_at")} : {formatDateTime(play.created_at)}
                      </p>
                    </div>

                    {/* Badge équipe */}
                    {team ? (
                      <span
                        className="shrink-0 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-violet-100 text-violet-800 text-xs font-medium
                                 dark:bg-violet-900/30 dark:text-violet-200"
                        title={t("allplays:card.team_assigned_title")}
                      >
                        <Users className="w-3.5 h-3.5" />
                        <span className="max-w-[120px] truncate">{team.team_name}</span>
                      </span>
                    ) : (
                      <span
                        className="shrink-0 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium
                                 dark:bg-gray-800 dark:text-gray-300"
                        title={t("allplays:card.no_team_title")}
                      >
                        <Users className="w-3.5 h-3.5" />
                        {t("allplays:card.none")}
                      </span>
                    )}
                  </div>

                  {/* Tags */}
                  {play.tag_ids && play.tag_ids.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {tags
                        .filter((tag) => play.tag_ids?.includes(tag.id))
                        .slice(0, 10)
                        .map((tag) => (
                          <span
                            key={tag.id}
                            className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800
                                     dark:bg-blue-900/40 dark:text-blue-300"
                          >
                            {tag.name}
                          </span>
                        ))}
                    </div>
                  )}

                  <button
                    className="mt-4 w-full bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-xl
                             focus:outline-none focus:ring-2 focus:ring-blue-400/60"
                    onClick={(e) => { e.stopPropagation(); handleView(play.id); }}
                  >
                    {t("allplays:card.view")}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
